/**
 * SearchInput Component
 *
 * A customizable search input with debounced search, dropdown results,
 * keyboard navigation, and support for custom value creation.
 *
 * Component Hierarchy:
 * SearchInput
 * ├── SearchContainer
 * │   ├── Input (atomic component)
 * │   └── SearchDropdown (conditional)
 * │       └── SearchResultsList
 * │           ├── SearchResultMessage (loading state)
 * │           ├── SearchResultMessage (error state)
 * │           ├── SearchResultMessage (empty state)
 * │           └── SearchResultItem[]
 * │               ├── BodySmall (label)
 * │               ├── BodySubtext (subtext - optional)
 * │               └── rightContent (string or ReactNode - optional)
 *
 * Helper Functions:
 * ├── handleInput: Manages input changes and dropdown visibility
 * ├── handleResultClick: Handles option selection via mouse
 * ├── handleKeyDown: Manages keyboard navigation (Arrow keys, Enter, Escape)
 * ├── handleAccountSelect: Processes selected account with metadata
 * ├── performSearch: Executes debounced search with loading states
 * ├── displayOptions: Computes final options including "Create new..." when applicable
 *
 * @example
 * <SearchInput
 *   value={searchTerm}
 *   onChange={setSearchTerm}
 *   onSearch={async (term) => fetchAccounts(term)}
 *   onSelect={(option) => handleSelection(option)}
 *   placeholder="Search accounts..."
 *   allowCustomValue={true}
 *   debounceMs={300}
 * />
 */
import type React from 'react'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Button, { ButtonVariant } from '@/components/atomic/button'
import { ButtonSize } from '@/components/atomic/button/variants'
import Icon, { IconName } from '@/components/atomic/icon'
import Input from '@/components/atomic/input'
import { BodySmall, BodySubtext } from '@/components/atomic/text'
import { KeyboardKeys } from '@/utils/keyboard-events/keys'
import { cn } from '@/utils/shadcn/utils'

import {
  SearchContainer,
  SearchDropdown,
  SearchResultItem,
  SearchResultMessage,
  SearchResultsList,
} from './styles'
import {
  type SearchInputProps,
  SearchInputSize,
  type SearchOption,
  SearchResultMessageVariant,
} from './types'
import { useSearch } from './use-search'

export function SearchInput({
  value,
  onChange,
  onSelect,
  onSearch,
  debounceMs = 300,
  minSearchLength = 1,
  placeholder = 'Search...',
  size = SearchInputSize.LARGE,
  disabled = false,
  required = false,
  isLoading: externalIsLoading = false,
  loadingMessage = 'Loading...',
  emptyMessage = 'No results found',
  errorMessage = 'Error occurred while searching',
  shortSearchMessage = 'Type to search...',
  className,
  dropdownClassName,
  containerClassName,
  maxResults,
  renderOption,
  disableDropdown = false,
  showOptionsOnFocus = false,
  clearOnSelect = false,
  onDropdownOpen,
}: SearchInputProps) {
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLUListElement>(null)
  const [debouncedTerm, setDebouncedTerm] = useState(value)

  // Use custom hook for search logic
  const {
    isLoading: internalIsLoading,
    results,
    error,
  } = useSearch(
    debouncedTerm,
    onSearch,
    minSearchLength,
    maxResults,
    showOptionsOnFocus,
  )

  const isLoading = useMemo(
    () => externalIsLoading || internalIsLoading,
    [externalIsLoading, internalIsLoading],
  )

  // Debounce input
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedTerm(value), debounceMs)
    return () => clearTimeout(timeout)
  }, [value, debounceMs])

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      onChange(val)
      const shouldOpen = showOptionsOnFocus
        ? true
        : !!val || debouncedTerm.length >= minSearchLength

      if (shouldOpen && !open && onDropdownOpen) {
        onDropdownOpen()
      }
      setOpen(shouldOpen)
    },
    [
      onChange,
      debouncedTerm.length,
      minSearchLength,
      showOptionsOnFocus,
      open,
      onDropdownOpen,
    ],
  )

  const handleResultClick = useCallback(
    (option: SearchOption, event?: React.MouseEvent) => {
      if (event) event.preventDefault()

      onChange(clearOnSelect ? '' : option.value)
      if (onSelect) onSelect(option)
      setOpen(false)
      inputRef.current?.blur()
    },
    [clearOnSelect, onChange, onSelect],
  )

  const handleFocus = useCallback(() => {
    const shouldOpen = showOptionsOnFocus
      ? true
      : value.length > 0 || debouncedTerm.length >= minSearchLength

    if (shouldOpen && !open && onDropdownOpen) {
      onDropdownOpen()
    }
    setOpen(shouldOpen)
  }, [
    showOptionsOnFocus,
    value.length,
    debouncedTerm.length,
    minSearchLength,
    open,
    onDropdownOpen,
  ])

  const handleBlur = useCallback(() => setOpen(false), [])

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex < 0 || !resultsRef.current) return
    const el = resultsRef.current.children[focusedIndex] as
      | HTMLElement
      | undefined
    el?.scrollIntoView({ block: 'nearest' })
  }, [focusedIndex])

  // Build final display options
  const displayOptions = useMemo(() => {
    // If dropdown is disabled, return empty array
    if (disableDropdown) {
      return []
    }

    return results
  }, [disableDropdown, results])

  const displayOptionsLength = useMemo(
    () => displayOptions.length,
    [displayOptions.length],
  )

  // Factory for result click handlers
  const createResultClickHandler = useCallback(
    (option: SearchOption) => (e: React.MouseEvent) =>
      handleResultClick(option, e),
    [handleResultClick],
  )

  // Update keyboard navigation to account for create new option
  // biome-ignore lint/correctness/useExhaustiveDependencies: Bulk suppression as part of biome migration
  useEffect(() => {
    setFocusedIndex(displayOptionsLength > 0 ? 0 : -1)
  }, [displayOptionsLength, open])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open || displayOptionsLength === 0) return

      if (e.key === KeyboardKeys.ARROW_DOWN) {
        e.preventDefault()
        setFocusedIndex((i) => (i < displayOptionsLength - 1 ? i + 1 : 0))
      } else if (e.key === KeyboardKeys.ARROW_UP) {
        e.preventDefault()
        setFocusedIndex((i) => (i > 0 ? i - 1 : displayOptionsLength - 1))
      } else if (e.key === KeyboardKeys.ENTER) {
        e.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < displayOptionsLength) {
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          handleResultClick(displayOptions[focusedIndex]!)
        }
      } else if (e.key === KeyboardKeys.ESCAPE) {
        setOpen(false)
      }
    },
    [
      open,
      displayOptionsLength,
      focusedIndex,
      handleResultClick,
      displayOptions,
    ],
  )

  const shouldShowDropdown = useMemo(
    () =>
      !disableDropdown &&
      (showOptionsOnFocus
        ? open
        : open &&
          (value.length > 0 || debouncedTerm.length >= minSearchLength)),
    [
      disableDropdown,
      showOptionsOnFocus,
      open,
      value.length,
      debouncedTerm.length,
      minSearchLength,
    ],
  )

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      onChange('')
      setOpen(false)
      inputRef.current?.focus()
    },
    [onChange],
  )

  const showClearButton = value.length > 0

  const clearButton = useMemo(
    () =>
      showClearButton ? (
        <Button
          type="button"
          variant={ButtonVariant.Ghost}
          size={ButtonSize.Small}
          icon={
            <Icon
              iconName={IconName.XMarkIcon}
              size="small"
              className="text-tertiary dark:text-secondary"
            />
          }
          onClick={handleClear}
        />
      ) : undefined,
    [showClearButton, handleClear],
  )

  return (
    <SearchContainer className={cn('max-w-[244px]', containerClassName)}>
      <Input
        ref={inputRef}
        required={required}
        size={size}
        placeholder={placeholder}
        value={value}
        onChange={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={className}
        autoComplete="off"
        disabled={disabled}
        slotProps={
          clearButton
            ? {
                side: 'right',
                showSeparator: false,
                icon: clearButton,
              }
            : undefined
        }
      />
      {shouldShowDropdown && (
        <SearchDropdown className={dropdownClassName}>
          <SearchResultsList ref={resultsRef}>
            {isLoading && (
              <SearchResultMessage variant={SearchResultMessageVariant.LOADING}>
                {loadingMessage}
              </SearchResultMessage>
            )}
            {error && (
              <SearchResultMessage variant={SearchResultMessageVariant.ERROR}>
                {errorMessage}
              </SearchResultMessage>
            )}
            {!isLoading &&
              !error &&
              debouncedTerm.length > 0 &&
              debouncedTerm.length < minSearchLength && (
                <SearchResultMessage variant={SearchResultMessageVariant.EMPTY}>
                  {shortSearchMessage}
                </SearchResultMessage>
              )}
            {!isLoading &&
              !error &&
              displayOptionsLength === 0 &&
              debouncedTerm.length >= minSearchLength && (
                <SearchResultMessage variant={SearchResultMessageVariant.EMPTY}>
                  {emptyMessage}
                </SearchResultMessage>
              )}
            {!isLoading &&
              !error &&
              displayOptions.map((option, idx) => (
                <SearchResultItem
                  key={`${option.value}-${idx}`}
                  isFocused={focusedIndex === idx}
                  onMouseDown={createResultClickHandler(option)}
                >
                  {renderOption ? (
                    renderOption(option, {
                      isFocused: focusedIndex === idx,
                    })
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center">
                          <BodySmall className="truncate">
                            {option.label}
                          </BodySmall>
                          {option.labelIcon && (
                            <div className="flex-shrink-0 ml-1">
                              {option.labelIcon}
                            </div>
                          )}
                        </div>
                        {option.subtext && (
                          <BodySubtext className="text-secondary truncate">
                            {option.subtext}
                          </BodySubtext>
                        )}
                      </div>
                      {option.rightContent && (
                        <div className="flex-shrink-0 ml-2">
                          {typeof option.rightContent === 'string' ? (
                            <BodySubtext className="text-secondary">
                              {option.rightContent}
                            </BodySubtext>
                          ) : (
                            option.rightContent
                          )}
                        </div>
                      )}
                    </>
                  )}
                </SearchResultItem>
              ))}
          </SearchResultsList>
        </SearchDropdown>
      )}
    </SearchContainer>
  )
}
